import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Form, Col, Row, Input, message, Radio, Typography, Collapse } from 'antd';
import { FormItem, Button, Editable, ImageInput, Avatar, SearchBox, DatePicker } from 'components/elements';
import { useEditFamilyRelationAtOnceMutation, FamilyTreeAttributes, FamilyTree, PartnerPosition } from 'graphql/types';
import { ResizeImageResult, ParseGoogleAddressResult } from 'helpers';
import moment from 'moment';
import { omit } from 'lodash';

type ZendeginamehValues = {
  nickname?: string;
  placeOfBirth?: string;
  placeOfDeath?: string;
  finalDisposition?: string;
  sedrehPushiOrNavjoteDate?: moment.Moment;
  marriageDate?: moment.Moment;
  marriageLocation?: string;
  childrenAndBirthplaces?: string;
  lineageAndCommunity?: string;
  ancestralOrigin?: string;
  domicileAndMigration?: string;
  languagesAndPurpose?: string;
  educationAndDegrees?: string;
  occupations?: string;
  achievementsAndAwards?: string;
  membershipsAndRoles?: string;
  notableTraditions?: string;
  storiesAndMemories?: string;
  favoriteSayings?: string;
  mediaAndRecordings?: string;
  interviewerContributor?: string;
};

interface FromFields {
  fullName?: string;
  partnerId?: string;
  partnerPosition?: PartnerPosition;
  avatar?: ResizeImageResult;
  birthdate?: moment.Moment;
  deathdate?: moment.Moment;
  bio?: string;
  currentLocation?: ParseGoogleAddressResult;
  birthLocation?: ParseGoogleAddressResult;
  deathLocation?: ParseGoogleAddressResult;
  zendeginameh?: ZendeginamehValues;
}

interface EditViewTreePanelProps {
  data: FamilyTree[];
  refetch: () => void;
}

export interface OpenDrawerProps {
  id: string;
  isEditing: boolean;
}

const DATE_FORMAT = 'YYYY-MM-DD';

const ZENDEGINAMEH_DATE_FIELDS = ['sedrehPushiOrNavjoteDate', 'marriageDate'] as const;

const ZENDEGINAMEH_TEXT_FIELDS = [
  'nickname',
  'placeOfBirth',
  'placeOfDeath',
  'finalDisposition',
  'marriageLocation',
  'childrenAndBirthplaces',
  'lineageAndCommunity',
  'ancestralOrigin',
  'domicileAndMigration',
  'languagesAndPurpose',
  'educationAndDegrees',
  'occupations',
  'achievementsAndAwards',
  'membershipsAndRoles',
  'notableTraditions',
  'storiesAndMemories',
  'favoriteSayings',
  'mediaAndRecordings',
  'interviewerContributor',
] as const;

/** Build the Zendegīnāmeh form values from a family tree node. */
const parseZendeginameh = (node?: FamilyTree | null): ZendeginamehValues => {
  if (!node) {
    return {};
  }

  const values: ZendeginamehValues = {};

  for (const field of ZENDEGINAMEH_TEXT_FIELDS) {
    values[field] = node[field] || undefined;
  }
  for (const field of ZENDEGINAMEH_DATE_FIELDS) {
    values[field] = node[field] ? moment(node[field]) : undefined;
  }

  return values;
};

/** Map the Zendegīnāmeh form values onto the mutation attributes, sending every param. */
const zendeginamehAttributes = (values?: ZendeginamehValues | null): FamilyTreeAttributes => {
  const attributes: FamilyTreeAttributes = {};

  if (!values) {
    return attributes;
  }

  for (const field of ZENDEGINAMEH_TEXT_FIELDS) {
    const value = values[field];
    attributes[field] = value ? String(value).trim() : null;
  }
  for (const field of ZENDEGINAMEH_DATE_FIELDS) {
    const value = values[field];
    attributes[field] = value ? moment(value).format(DATE_FORMAT) : null;
  }

  return attributes;
};

interface ZendeginamehFieldProps {
  name: keyof ZendeginamehValues;
  label: string;
  isEditable?: boolean;
  placeholder?: string;
  minRows?: number;
}

const TextField = ({ name, label, isEditable, placeholder }: ZendeginamehFieldProps) => (
  <FormItem theme="dark" name={['zendeginameh', name]} label={label}>
    <Editable
      isEditable={isEditable}
      preview={({ value }) => <p>{value}</p>}
      input={(props) => <Input placeholder={placeholder} {...props} />}
    />
  </FormItem>
);

const TextAreaField = ({ name, label, isEditable, minRows = 2 }: ZendeginamehFieldProps) => (
  <FormItem theme="dark" name={['zendeginameh', name]} label={label}>
    <Editable
      isEditable={isEditable}
      preview={({ value }) => <p style={{ whiteSpace: 'pre-wrap' }}>{value}</p>}
      input={(props) => <Input.TextArea autoSize={{ minRows }} {...props} />}
    />
  </FormItem>
);

const DateField = ({ name, label, isEditable }: ZendeginamehFieldProps) => (
  <FormItem theme="dark" name={['zendeginameh', name]} label={label}>
    <Editable
      isEditable={isEditable}
      preview={({ value }) => <p>{moment.isMoment(value) ? value.format('ll') : ''}</p>}
      input={(props) => <DatePicker {...props} placeholder={DATE_FORMAT} />}
    />
  </FormItem>
);

const EditViewTreePanel = ({ data, refetch }: EditViewTreePanelProps) => {
  const [drawerSetting, setDrawerSetting] = useState({ visible: false, id: '', isEditing: false });
  const [form] = Form.useForm();
  const [editFamilyRelation, { loading }] = useEditFamilyRelationAtOnceMutation();

  const pageReset = useCallback(() => {
    setDrawerSetting({ visible: false, id: '', isEditing: false });
    form.resetFields();
  }, [form]);

  const dataById = useMemo(() => {
    return data.reduce<Record<string, FamilyTree>>(
      (acc, item) => ({
        ...acc,
        [item.id]: { ...item },
      }),
      {},
    );
  }, [data]);

  const selectedItem = useMemo(() => {
    return dataById[drawerSetting.id];
  }, [dataById, drawerSetting.id]);

  useEffect(() => {
    if (selectedItem) {
      const {
        fullName,
        partnerPosition,
        isPartner,
        pid,
        ppid,
        avatarUrl,
        birthdate,
        deathdate,
        bio,
        currentLocation,
        deathLocation,
        birthLocation,
      } = selectedItem;

      const formFields: FromFields = {
        fullName,
        avatar: { blob: null, url: avatarUrl },
        bio,
        currentLocation: currentLocation?.data?.attributes ? omit(currentLocation.data.attributes, '__typename') : null,
        birthLocation: birthLocation?.data?.attributes ? omit(birthLocation.data.attributes, '__typename') : null,
        deathLocation: deathLocation?.data?.attributes ? omit(deathLocation.data.attributes, '__typename') : null,
        zendeginameh: parseZendeginameh(selectedItem),
      };

      if (isPartner) {
        formFields.partnerPosition = partnerPosition;
      }
      if (!isPartner && pid) {
        // is child
        formFields.partnerId = ppid || pid;
      }
      if (birthdate) {
        formFields.birthdate = moment(birthdate);
      }
      if (deathdate) {
        formFields.deathdate = moment(deathdate);
      }

      form.setFieldsValue(formFields);
    }
  }, [form, selectedItem]);

  const isChild = useMemo(() => {
    if (selectedItem) {
      return !selectedItem.isPartner && selectedItem.pid;
    }
    return false;
  }, [selectedItem]);

  const findPotentialParents = useCallback(() => {
    const currentParentId = selectedItem.pid;
    const partners = data.filter((node) => {
      return node.isPartner && node.pid === currentParentId;
    });
    return partners;
  }, [data, selectedItem]);

  const parentOptions = useMemo(() => {
    if (isChild) {
      const currentParent = dataById[selectedItem.pid];
      const partners = findPotentialParents();
      const optionResult = partners.map((partner) => (
        <Radio value={partner.id}>
          {currentParent.fullName} and {partner.fullName}
        </Radio>
      ));

      optionResult.unshift(<Radio value={currentParent.id}>{currentParent.fullName}</Radio>);

      return optionResult;
    }
    return null;
  }, [dataById, findPotentialParents, isChild, selectedItem]);

  const openDrawer = useCallback(({ id, isEditing }: OpenDrawerProps) => {
    setDrawerSetting({ visible: true, id, isEditing });
  }, []);

  const editProfile = useCallback(() => {
    setDrawerSetting({ ...drawerSetting, isEditing: true });
  }, [drawerSetting]);

  const onClose = useCallback(() => {
    pageReset();
  }, [pageReset]);

  const onSubmit = useCallback(() => {
    form.submit();
  }, [form]);

  const onFinish = useCallback(
    async (values: FromFields) => {
      const { partnerId, avatar, zendeginameh, ...restValues } = values;
      const attributes: FamilyTreeAttributes = {
        ...restValues,
        ...zendeginamehAttributes(zendeginameh),
      };

      if (avatar?.blob) {
        attributes.avatar = avatar?.blob;
      }

      if (isChild && partnerId) {
        const selectedParent = dataById[partnerId];
        if (selectedParent.isPartner) {
          attributes.parents = [partnerId, selectedItem.pid];
        } else {
          attributes.parents = [partnerId];
        }
      }

      try {
        await editFamilyRelation({
          variables: { input: attributes, treeNodeId: selectedItem.id },
        });
        pageReset();
        refetch();
      } catch (err) {
        if (err instanceof Error) {
          message.error(err.message);
        }
      }
    },
    [dataById, editFamilyRelation, isChild, pageReset, refetch, selectedItem],
  );

  const drawerPanel = {
    title: `${drawerSetting.isEditing ? 'Edit' : 'View'} ${selectedItem?.fullName} Profile`,
    onClose,
    visible: drawerSetting.visible,
    footer: drawerSetting.isEditing ? (
      <div
        style={{
          textAlign: 'right',
        }}
      >
        <Button onClick={onClose} style={{ marginRight: 8 }} type="primary" ghost>
          Cancel
        </Button>
        <Button onClick={onSubmit} type="primary" loading={loading}>
          Submit
        </Button>
      </div>
    ) : (
      <div
        style={{
          textAlign: 'right',
        }}
      >
        <Button onClick={editProfile} type="primary" ghost>
          Edit Profile
        </Button>
      </div>
    ),
    children: (
      <div>
        <Form
          layout="vertical"
          //   validateTrigger="onChange"
          preserve={false}
          form={form}
          onFinish={onFinish}
        >
          <Typography.Title level={5}>Zendegīnāmeh</Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Please fill in only what you know, and leave unknown details blank.
          </Typography.Paragraph>

          <Row gutter={16} justify="center">
            <FormItem name="avatar" noStyle bordered>
              <Editable
                isEditable={drawerSetting.isEditing}
                preview={({ value }) => <Avatar avatarUrl={value?.url} size="lg" center noOutline />}
                input={({ value, onChange }) => (
                  <ImageInput width={200} height={200} cropping onChange={onChange}>
                    <Avatar avatarUrl={value?.url} size="lg" editable center noOutline />
                  </ImageInput>
                )}
              />
            </FormItem>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <FormItem
                theme="dark"
                name="fullName"
                label="Full Name"
                rules={[{ required: true, message: 'Please input your Full Name!' }]}
              >
                <Editable
                  isEditable={drawerSetting.isEditing}
                  preview={({ value }) => <p>{value}</p>}
                  input={(props) => <Input {...props} />}
                />
              </FormItem>
            </Col>

            <Col xs={24} sm={12}>
              {selectedItem?.isPartner && (
                <FormItem theme="dark" name="partnerPosition" label="Which Side To Display?">
                  <Radio.Group>
                    <Radio value="left">Left</Radio>
                    <Radio value="right">Right</Radio>
                  </Radio.Group>
                </FormItem>
              )}
            </Col>
          </Row>
          {isChild && drawerSetting.isEditing && (
            <Row>
              <Col span={24}>
                <FormItem
                  theme="dark"
                  name="partnerId"
                  label="Who are the parents?"
                  rules={[{ required: true, message: 'Please select one from the list!' }]}
                >
                  <Radio.Group>{parentOptions}</Radio.Group>
                </FormItem>
              </Col>
            </Row>
          )}
          <Row gutter={16}>
            <Col span={24}>
              <FormItem theme="dark" name="currentLocation" label="Current Location">
                <Editable
                  isEditable={drawerSetting.isEditing}
                  preview={({ value }) => <p>{value ? value.address : ''}</p>}
                  input={(props) => <SearchBox {...props} />}
                />
              </FormItem>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <FormItem theme="dark" name="birthdate" label="Birth Date">
                <Editable
                  isEditable={drawerSetting.isEditing}
                  preview={({ value }) => <p>{value ? value.format('ll') : ''}</p>}
                  input={(props) => <DatePicker {...props} placeholder="YYYY-MM-DD" />}
                />
              </FormItem>
            </Col>
            <Col xs={24} sm={12}>
              <FormItem theme="dark" name="deathdate" label="Death Date">
                <Editable
                  isEditable={drawerSetting.isEditing}
                  preview={({ value }) => <p>{value ? value.format('ll') : ''}</p>}
                  input={(props) => <DatePicker {...props} placeholder="YYYY-MM-DD" />}
                />
              </FormItem>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <FormItem theme="dark" name="birthLocation" label="Birth Location">
                <Editable
                  isEditable={drawerSetting.isEditing}
                  preview={({ value }) => <p>{value ? value.address : ''}</p>}
                  input={(props) => <SearchBox {...props} />}
                />
              </FormItem>
            </Col>
            <Col xs={24} sm={12}>
              <FormItem name="deathLocation" label="Death Location">
                <Editable
                  isEditable={drawerSetting.isEditing}
                  preview={({ value }) => <p>{value ? value.address : ''}</p>}
                  input={(props) => <SearchBox {...props} />}
                />
              </FormItem>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <FormItem name="bio" label="Biography" theme="dark">
                <Editable
                  isEditable={drawerSetting.isEditing}
                  preview={({ value }) => <p>{value}</p>}
                  input={(props) => (
                    <Input.TextArea placeholder="Enter brief biography here..." autoSize={{ minRows: 5 }} {...props} />
                  )}
                />
              </FormItem>
            </Col>
          </Row>

          <Collapse ghost>
            <Collapse.Panel header="Details" key="details" forceRender>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <TextField name="nickname" label="Nickname" isEditable={drawerSetting.isEditing} />
                </Col>
                <Col xs={24} sm={12}>
                  <TextField
                    name="finalDisposition"
                    label="Final disposition"
                    placeholder="Burial, Cremation, Dakhme, Other, or Unknown"
                    isEditable={drawerSetting.isEditing}
                  />
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <TextField name="placeOfBirth" label="Place of birth" isEditable={drawerSetting.isEditing} />
                </Col>
                <Col xs={24} sm={12}>
                  <TextField name="placeOfDeath" label="Place of death" isEditable={drawerSetting.isEditing} />
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <DateField
                    name="sedrehPushiOrNavjoteDate"
                    label="Date of Sedreh Pūshi or Navjōte"
                    isEditable={drawerSetting.isEditing}
                  />
                </Col>
                <Col xs={24} sm={12}>
                  <DateField name="marriageDate" label="Date of marriage" isEditable={drawerSetting.isEditing} />
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <TextField name="marriageLocation" label="Location of marriage" isEditable={drawerSetting.isEditing} />
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <TextAreaField
                    name="childrenAndBirthplaces"
                    label="Children’s names and where born"
                    isEditable={drawerSetting.isEditing}
                  />
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <TextAreaField
                    name="lineageAndCommunity"
                    label="Lineage and community"
                    isEditable={drawerSetting.isEditing}
                  />
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <TextField
                    name="ancestralOrigin"
                    label="Place of ancestral origin"
                    isEditable={drawerSetting.isEditing}
                  />
                </Col>
                <Col xs={24} sm={12}>
                  <TextField
                    name="domicileAndMigration"
                    label="Domicile and migration history"
                    isEditable={drawerSetting.isEditing}
                  />
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <TextAreaField
                    name="languagesAndPurpose"
                    label="Languages spoken and what they felt their purpose was / what Zoroastrianism meant to them"
                    isEditable={drawerSetting.isEditing}
                  />
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <TextAreaField
                    name="educationAndDegrees"
                    label="Primary school, high school, college, and other education / degrees"
                    isEditable={drawerSetting.isEditing}
                  />
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <TextAreaField name="occupations" label="Occupations" isEditable={drawerSetting.isEditing} />
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <TextAreaField
                    name="achievementsAndAwards"
                    label="Notable achievements and awards"
                    isEditable={drawerSetting.isEditing}
                  />
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <TextAreaField
                    name="membershipsAndRoles"
                    label="Memberships and notable roles"
                    isEditable={drawerSetting.isEditing}
                  />
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <TextAreaField
                    name="notableTraditions"
                    label="Notable traditions"
                    isEditable={drawerSetting.isEditing}
                  />
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <TextAreaField
                    name="storiesAndMemories"
                    label="Notable stories and memories with dates and places"
                    minRows={3}
                    isEditable={drawerSetting.isEditing}
                  />
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <TextAreaField name="favoriteSayings" label="Favorite sayings" isEditable={drawerSetting.isEditing} />
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <TextAreaField
                    name="mediaAndRecordings"
                    label="Photos, videos, and recordings"
                    isEditable={drawerSetting.isEditing}
                  />
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <TextAreaField
                    name="interviewerContributor"
                    label="Interviewer / contributor name, relationship to subject, and date and place"
                    isEditable={drawerSetting.isEditing}
                  />
                </Col>
              </Row>
            </Collapse.Panel>
          </Collapse>
        </Form>
      </div>
    ),
  };

  return [drawerPanel, openDrawer] as const;
};

export default EditViewTreePanel;
